import { useCallback, useEffect, useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { loadStripe } from "@stripe/stripe-js";
import { useQuery } from "@tanstack/react-query";
import { AnimatePresence, m } from "framer-motion";
import { AlertCircle, ArrowRight, Check, CreditCard, Loader2, Shield, Wallet } from "lucide-react";

import {
  CardActionOutcomeUnknown,
  CardActionStillProcessing,
  confirmCardAction,
  discardPendingPayment,
  type DiscardOutcome,
  pendingChangePayment,
  sendAfterCardAction,
} from "@/components/billing/confirm-card-action";
import { usePendingPaymentPrompt } from "@/components/billing/pending-payment-sheet";
import {
  GoToServicePauseButton,
  isServicesPausedError,
  usePauseCarryBillingMessage,
  useServicesPausedBillingMessage,
} from "@/components/billing/services-paused-billing";
import { Button } from "@/components/ui/button";
import { ResponsiveSheet } from "@/components/ui/responsive-sheet";
import { isVaultUnlockCancelled, useProtectedAction } from "@/lib/hooks/use-protected-action";

// Source: legacy src/components/billing/one-click-payment-modal.tsx (1:1 port
// of the preview → confirm → execute flow). Differences vs legacy, per SPA
// conventions: the hand-rolled portal/backdrop is replaced by
// <ResponsiveSheet> (legacy h2 title + X button live in the sheet header),
// raw fetch() → apiFetch + react-query for the preview call, and the Stripe
// publishable key comes from import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY
// (same source as stripe-elements-provider.tsx). The pay_invoice/addon
// action variants of the legacy modal are not ported — this SPA entry point
// only needs upgrade / reactivate / retry_payment.
//
// VAULT PIN (2026-10): POST /one-click-pay — preview included, it names the
// card on file — answers 403 vault_locked while a PIN guarding "billing" or
// "wallet" is not unlocked. The caller asks for the PIN before opening this modal; both
// calls also go through runProtected, so an unlock that lapsed meanwhile asks
// again and retries once. Dismissing that gate closes the modal (preview) or
// returns to the confirmation (pay).

const BRAND_LABELS: Record<string, string> = {
  visa: "Visa",
  mastercard: "Mastercard",
  amex: "Amex",
  discover: "Discover",
};

const ONE_CLICK_PAY_PATH = "/api/account/billing/one-click-pay";

// Mirrors OneClickPreviewOut in apps/api/src/routes/account-billing.ts
// (legacy: OneClickPreview from @/modules/billing/one-click-pay).
interface OneClickPreview {
  amount: number;
  currency: string;
  description: string;
  card: { brand: string; last4: string } | null;
  walletCredit: number;
  cardAmount: number;
}

// Mirrors OneClickResult in apps/api/src/routes/account-billing.ts.
interface OneClickResult {
  success: boolean;
  requiresAction?: boolean;
  clientSecret?: string;
  /** The active Stripe mode's key, sent with `requiresAction`. */
  publishableKey?: string | null;
  /** A reactivation waiting for 3-D Secure: its subscription (abandoned if not confirmed). */
  stripeSubscriptionId?: string;
  amount: number;
  currency: string;
  walletDeducted?: number;
  cardCharged?: number;
  error?: string;
}

type ModalState =
  | "loading"
  | "no_card"
  | "confirm"
  | "processing"
  | "three_ds"
  | "success"
  | "error";

// Post-confirm phase, driven imperatively (legacy setState calls). The
// pre-confirm states (loading / no_card / confirm) are derived from the
// react-query preview while phase === "review".
type Phase = "review" | "processing" | "three_ds" | "success" | "no_card" | "error";

interface OneClickPaymentModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  action: "upgrade" | "reactivate" | "retry_payment";
  subscriptionId?: string;
  newPlanCode?: string;
  /** 409 SERVICES_PAUSED: the caller closes the modal and shows its pause card. */
  onShowServicePause?: () => void;
}

export function OneClickPaymentModal({
  open,
  onClose,
  onSuccess,
  action,
  subscriptionId,
  newPlanCode,
  onShowServicePause,
}: OneClickPaymentModalProps) {
  const { t } = useLingui();
  const [phase, setPhase] = useState<Phase>("review");
  const [error, setError] = useState<string | null>(null);
  // The charge was refused with SERVICES_PAUSED — the error state offers the way out.
  const [pausedRefusal, setPausedRefusal] = useState(false);
  const servicesPausedMessage = useServicesPausedBillingMessage();
  const pauseCarryMessage = usePauseCarryBillingMessage();
  const { runProtected, PinGateElement } = useProtectedAction(["billing", "wallet"]);
  const pendingPrompt = usePendingPaymentPrompt();

  // The refusals the API names with a `code` (2026-10 money review) — nothing
  // was charged for any of them. Anything else falls back to its message.
  const codeCopy = useCallback(
    (code: string | undefined, err?: unknown): string | null => {
      switch (code) {
        case "subscription_busy":
          return t`Another change to this subscription is in progress. Please try again in a moment.`;
        case "subscription_changed":
          return t`This subscription changed in the meantime. Reload and try again.`;
        case "subscription_lease_unavailable":
          return t`Billing is temporarily unavailable. Please try again in a moment.`;
        case "founding_upgrade_unavailable":
          return t`This change is not available on your Founding plan. Please contact support.`;
        case "payment_failed":
          return t`The payment for this change did not go through, so nothing was changed.`;
        case "payment_not_returned":
          return t`This subscription changed in the meantime and the payment could not be returned automatically. Our team will refund it — please do not try again.`;
        case "payment_processing":
          return t`A payment for this subscription is still being processed. Please try again in a moment.`;
        case "plan_change_scheduled":
          return t`Cancel the scheduled plan change first.`;
        case "SERVICES_PAUSED":
          return servicesPausedMessage;
        case "PAUSE_CARRY_ACTIVE":
          return pauseCarryMessage(err);
        default:
          return null;
      }
    },
    [t, servicesPausedMessage, pauseCarryMessage],
  );

  const buildBody = useCallback(
    (isPreview: boolean) => {
      const base: Record<string, unknown> = { action, preview: isPreview };
      if (subscriptionId) base.subscriptionId = subscriptionId;
      if (newPlanCode) base.newPlanCode = newPlanCode;
      return base;
    },
    [action, subscriptionId, newPlanCode],
  );

  // Fetch preview on open (legacy useEffect+fetch → react-query).
  const previewQuery = useQuery({
    queryKey: [
      "api",
      ONE_CLICK_PAY_PATH,
      "preview",
      action,
      subscriptionId ?? null,
      newPlanCode ?? null,
    ],
    queryFn: () =>
      runProtected(() => apiFetch.post<OneClickPreview>(ONE_CLICK_PAY_PATH, buildBody(true))),
    enabled: open,
    retry: false,
    staleTime: 0,
  });
  const preview = previewQuery.data ?? null;

  // The PIN gate was dismissed before the preview could load: nothing to show.
  const previewCancelled = !previewQuery.isFetching && isVaultUnlockCancelled(previewQuery.error);
  useEffect(() => {
    if (previewCancelled) onClose();
  }, [previewCancelled, onClose]);

  // Reset to the review phase on every open (legacy reset of state/preview/error).
  useEffect(() => {
    if (!open) return;
    setPhase("review");
    setError(null);
  }, [open]);

  const finishSuccess = useCallback(() => {
    setPhase("success");
    setTimeout(() => {
      onSuccess();
      onClose();
    }, 2000);
  }, [onSuccess, onClose]);

  const handle3DSecure = useCallback(
    async (
      clientSecret: string,
      publishableKey?: string | null,
      /** A reactivation's subscription, given up when the customer does not confirm. */
      abandonStripeSubscriptionId?: string,
    ) => {
      // Not confirmed: the held purchase is given up, so the profile can be
      // bought again at once instead of counting as in progress for a day.
      const abandon = () =>
        abandonStripeSubscriptionId
          ? apiFetch
              .post("/api/checkout/charge-saved-card/abandon", {
                stripeSubscriptionId: abandonStripeSubscriptionId,
              })
              .catch(() => undefined)
          : Promise.resolve();
      try {
        // The API's key (the active Stripe mode's), else the build's.
        const key =
          publishableKey || (import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string | undefined);
        if (!key) {
          setError("Stripe not configured");
          setPhase("error");
          return;
        }

        const stripeJs = await loadStripe(key);
        if (!stripeJs) {
          setError("Failed to load Stripe");
          setPhase("error");
          return;
        }

        const { error: threeDsError } = await stripeJs.handleNextAction({ clientSecret });

        if (threeDsError) {
          await abandon();
          setError(threeDsError.message || "3D Secure failed");
          setPhase("error");
          return;
        }

        finishSuccess();
      } catch {
        await abandon();
        setError("3D Secure verification failed");
        setPhase("error");
      }
    },
    [finishSuccess],
  );

  const handlePay = useCallback(async () => {
    setError(null);
    setPhase("processing");

    const discard = (clientSecret: string): Promise<DiscardOutcome> =>
      subscriptionId
        ? discardPendingPayment(subscriptionId, runProtected, clientSecret)
        : Promise.resolve("unknown");
    /** Through, or not known: nothing is sent on top of it. */
    const unlessDiscarded = (outcome: DiscardOutcome) => {
      if (outcome === "went_through") throw new CardActionStillProcessing();
      if (outcome === "unknown") throw new CardActionOutcomeUnknown();
    };
    const pay = () =>
      runProtected(() => apiFetch.post<OneClickResult>(ONE_CLICK_PAY_PATH, buildBody(false)));
    try {
      let data: OneClickResult;
      try {
        data = await pay();
      } catch (err) {
        // An earlier upgrade Stripe still holds for its 3-D Secure: the
        // customer confirms it (it applies) or, declining, it is discarded —
        // then this one is sent.
        const earlier = action === "upgrade" ? pendingChangePayment(err) : null;
        if (!earlier) throw err;
        // Shown first — it may be an older change than this one.
        const choice = await pendingPrompt.ask(earlier);
        if (choice === "cancel") {
          setPhase("review");
          return;
        }
        if (
          choice === "discard" ||
          !(await confirmCardAction(earlier.clientSecret, earlier.publishableKey))
        ) {
          unlessDiscarded(await discard(earlier.clientSecret));
        }
        data = await sendAfterCardAction(pay);
      }

      if (data.requiresAction && data.clientSecret) {
        setPhase("three_ds");
        if (action !== "upgrade") {
          await handle3DSecure(
            data.clientSecret,
            data.publishableKey,
            action === "reactivate" ? data.stripeSubscriptionId : undefined,
          );
          return;
        }
        // An upgrade is held on Stripe until its payment is confirmed: once the
        // customer confirms it, the same upgrade is sent again — applied on
        // Stripe by then, it is recorded without a second charge.
        if (!(await confirmCardAction(data.clientSecret, data.publishableKey))) {
          // Discarded, so the next change is not refused for a day — unless
          // its payment went through after all.
          unlessDiscarded(await discard(data.clientSecret));
          setError(t`The payment was not confirmed, so nothing was changed.`);
          setPhase("error");
          return;
        }
        // Stripe may take a moment to apply it: `payment_processing` is retried.
        const recorded = await sendAfterCardAction(() =>
          runProtected(() => apiFetch.post<OneClickResult>(ONE_CLICK_PAY_PATH, buildBody(false))),
        );
        if (recorded.success) {
          finishSuccess();
          return;
        }
        setError(recorded.error || "Payment failed");
        setPhase("error");
        return;
      }

      if (data.success) {
        finishSuccess();
        return;
      }

      setError(data.error || "Payment failed");
      setPhase("error");
    } catch (err) {
      // The PIN gate was dismissed: nothing was charged — back to the confirmation.
      if (isVaultUnlockCancelled(err)) {
        setPhase("review");
        return;
      }
      if (err instanceof CardActionStillProcessing) {
        // The payment went through: the page refreshes as the change lands.
        onSuccess();
        setError(
          t`Your payment went through and is still being applied. Refresh in a moment to see the change.`,
        );
        setPhase("error");
        return;
      }
      if (err instanceof CardActionOutcomeUnknown) {
        onSuccess();
        setError(
          t`We could not confirm what happened to this payment. Refresh to see the latest state.`,
        );
        setPhase("error");
        return;
      }
      if (err instanceof ApiError) {
        const body = err.body as { error?: string; code?: string } | null;
        if (err.status === 402 || body?.error === "no_default_card") {
          setPhase("no_card");
          return;
        }
        setPausedRefusal(isServicesPausedError(err));
        setError(codeCopy(body?.code, err) ?? (body?.error || "Payment failed"));
        setPhase("error");
        return;
      }
      setError("Payment failed");
      setPhase("error");
    }
  }, [
    action,
    buildBody,
    codeCopy,
    finishSuccess,
    handle3DSecure,
    onSuccess,
    pendingPrompt,
    runProtected,
    subscriptionId,
    t,
  ]);

  // "Try again" from the error state: back to confirm (legacy
  // setState("confirm")); if the preview itself failed, refetch it.
  const handleRetry = useCallback(() => {
    setError(null);
    setPausedRefusal(false);
    setPhase("review");
    if (previewQuery.isError) void previewQuery.refetch();
  }, [previewQuery]);

  const formatCurrency = (amount: number, currency: string) => {
    const symbol = currency === "EUR" ? "€" : currency;
    return `${symbol}${amount.toFixed(2)}`;
  };

  const previewNoCard = previewQuery.error instanceof ApiError && previewQuery.error.status === 402;
  const previewErrorBody =
    previewQuery.error instanceof ApiError
      ? (previewQuery.error.body as { error?: string; code?: string } | null)
      : null;
  const previewErrorMessage = previewQuery.error
    ? previewQuery.error instanceof ApiError
      ? (codeCopy(previewErrorBody?.code, previewQuery.error) ??
        previewErrorBody?.error ??
        "Failed to load payment details")
      : "Failed to connect"
    : null;

  let state: ModalState;
  if (phase !== "review") {
    state = phase;
  } else if (previewQuery.isPending || previewQuery.isFetching || previewCancelled) {
    // (Cancelled: closing — no error flash on the way out.)
    state = "loading";
  } else if (previewQuery.isError) {
    state = previewNoCard ? "no_card" : "error";
  } else if (!preview?.card) {
    state = "no_card";
  } else {
    state = "confirm";
  }

  const errorMessage = error ?? previewErrorMessage;
  const servicesPaused =
    (error !== null && pausedRefusal) ||
    (error === null && isServicesPausedError(previewQuery.error));
  const last4 = preview?.card?.last4 || "****";

  const actionLabel = {
    upgrade: <Trans>Confirm upgrade</Trans>,
    reactivate: <Trans>Reactivate now</Trans>,
    retry_payment: <Trans>Retry payment</Trans>,
  }[action];

  // Legacy blocked backdrop click + hid the X while a charge is in flight.
  const busy = state === "processing" || state === "three_ds";

  return (
    <ResponsiveSheet
      open={open}
      onClose={onClose}
      title={<Trans>Confirm payment</Trans>}
      width="md"
      dismissible={!busy}
      ariaLabel={t`Confirm payment`}
      closeLabel={t`Close`}
    >
      <AnimatePresence mode="wait" initial={false}>
        {/* Loading */}
        {state === "loading" && (
          <m.div
            key="loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-col items-center py-8"
          >
            <Loader2 className="text-primary h-8 w-8 animate-spin" />
            <p className="text-muted-foreground mt-3 text-sm">
              <Trans>Loading payment details...</Trans>
            </p>
          </m.div>
        )}

        {/* No card */}
        {state === "no_card" && (
          <m.div
            key="no_card"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-col items-center py-8"
          >
            <CreditCard className="text-muted-foreground h-10 w-10" />
            <p className="text-foreground mt-3 text-sm font-medium">
              <Trans>No saved card</Trans>
            </p>
            <p className="text-muted-foreground mt-1 text-xs">
              <Trans>Add a payment card in the Cards section to continue.</Trans>
            </p>
            <Button className="mt-4 h-11" onClick={onClose}>
              <Trans>Add a card</Trans>
            </Button>
          </m.div>
        )}

        {/* Confirmation */}
        {state === "confirm" && preview && (
          <m.div
            key="confirm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-5"
          >
            <p className="text-muted-foreground text-sm">{preview.description}</p>

            {/* Card info */}
            <div className="border-border flex items-center gap-3 rounded-lg border p-3">
              <div className="bg-muted flex h-10 w-14 items-center justify-center rounded-lg text-xs font-bold">
                {preview.card ? BRAND_LABELS[preview.card.brand] || preview.card.brand : "Card"}
              </div>
              <div>
                <p className="text-foreground text-sm font-medium">
                  <Trans>Card •••• {last4}</Trans>
                </p>
                <p className="text-muted-foreground text-xs">
                  <Trans>Paying with</Trans>
                </p>
              </div>
            </div>

            {/* Amount breakdown */}
            <div className="border-border space-y-2 rounded-lg border p-4">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  <Trans>Total amount</Trans>
                </span>
                <span className="text-foreground font-medium">
                  {formatCurrency(preview.amount, preview.currency)}
                </span>
              </div>
              {preview.walletCredit > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                    <Wallet className="h-3.5 w-3.5" />
                    <Trans>Wallet credit</Trans>
                  </span>
                  <span className="text-emerald-600 dark:text-emerald-400">
                    -{formatCurrency(preview.walletCredit, preview.currency)}
                  </span>
                </div>
              )}
              <div className="border-border flex justify-between border-t pt-2 text-sm">
                <span className="text-foreground font-semibold">
                  <Trans>Card charge</Trans>
                </span>
                <span className="text-foreground font-semibold">
                  {formatCurrency(preview.cardAmount, preview.currency)}
                </span>
              </div>
            </div>

            <Button className="h-11 w-full" size="lg" onClick={handlePay}>
              <Shield className="mr-2 h-4 w-4" />
              {actionLabel}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </m.div>
        )}

        {/* Processing */}
        {state === "processing" && (
          <m.div
            key="processing"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-col items-center py-8"
          >
            <Loader2 className="text-primary h-8 w-8 animate-spin" />
            <p className="text-foreground mt-3 text-sm font-medium">
              <Trans>Processing payment...</Trans>
            </p>
          </m.div>
        )}

        {/* 3D Secure */}
        {state === "three_ds" && (
          <m.div
            key="three_ds"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-col items-center py-8"
          >
            <Shield className="text-primary h-8 w-8" />
            <p className="text-foreground mt-3 text-sm font-medium">
              <Trans>3D Secure verification</Trans>
            </p>
            <p className="text-muted-foreground mt-1 text-xs">
              <Trans>Complete your bank verification to finalize the payment.</Trans>
            </p>
          </m.div>
        )}

        {/* Success */}
        {state === "success" && (
          <m.div
            key="success"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-col items-center py-8"
          >
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-950">
              <Check className="h-7 w-7 text-emerald-600" />
            </div>
            <p className="text-foreground mt-3 text-sm font-semibold">
              <Trans>Payment successful!</Trans>
            </p>
            <p className="text-muted-foreground mt-1 text-xs">
              <Trans>Your payment has been processed successfully.</Trans>
            </p>
          </m.div>
        )}

        {/* Error */}
        {state === "error" && (
          <m.div
            key="error"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-col items-center py-8"
          >
            <div className="bg-destructive/10 flex h-14 w-14 items-center justify-center rounded-full">
              <AlertCircle className="text-destructive h-7 w-7" />
            </div>
            <p className="text-foreground mt-3 text-sm font-semibold">
              <Trans>Payment failed</Trans>
            </p>
            <p className="text-muted-foreground mt-1 text-center text-xs">{errorMessage}</p>
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              {servicesPaused && onShowServicePause ? (
                <GoToServicePauseButton onClick={onShowServicePause} />
              ) : (
                <Button variant="outline" className="h-11" onClick={handleRetry}>
                  <Trans>Try again</Trans>
                </Button>
              )}
              <Button variant="ghost" className="h-11" onClick={onClose}>
                <Trans>Close</Trans>
              </Button>
            </div>
          </m.div>
        )}
      </AnimatePresence>
      {PinGateElement}
      {pendingPrompt.sheet}
    </ResponsiveSheet>
  );
}
